import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import {
  listInvoices,
  type InvoiceStatus,
  type PublicInvoice,
} from '../../lib/invoices-api';
import { extractApiError } from '../../lib/api-client';
import { formatDate, formatRupiah } from '../../lib/format';
import { InvoiceStatusChip, INVOICE_STATUS_KEY } from './status-chip';
import { useT } from '../../i18n';

const STATUS_OPTIONS: ReadonlyArray<InvoiceStatus> = [
  'DRAFT',
  'OPEN',
  'PAID',
  'VOID',
  'OVERDUE',
];

type StatusFilter = InvoiceStatus | 'ALL';

const PAGE_SIZE = 50;

export function InvoicesListPage() {
  const t = useT();
  const [items, setItems] = useState<PublicInvoice[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadFirstPage(filter: StatusFilter) {
    setLoading(true);
    setError(null);
    try {
      const out = await listInvoices({
        limit: PAGE_SIZE,
        ...(filter !== 'ALL' ? { status: filter } : {}),
      });
      setItems(out.items);
      setNextCursor(out.nextCursor);
    } catch (e) {
      setError(extractApiError(e).message);
      setItems([]);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      const out = await listInvoices({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
      });
      setItems((prev) => [...prev, ...out.items]);
      setNextCursor(out.nextCursor);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    loadFirstPage(statusFilter);
  }, [statusFilter]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('inv.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('inv.subtitle')}
          </Typography>
        </div>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="invoice-status-filter">{t('inv.status')}</InputLabel>
            <Select
              labelId="invoice-status-filter"
              label={t('inv.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <MenuItem value="ALL">{t('inv.filterAll')}</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(INVOICE_STATUS_KEY[s])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/invoices/new"
          >
            {t('inv.newInvoice')}
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {statusFilter === 'ALL'
                  ? t('inv.emptyAll')
                  : t('inv.emptyFiltered', { status: t(INVOICE_STATUS_KEY[statusFilter]).toLowerCase() })}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('inv.colNumber')}</TableCell>
                      <TableCell sx={{ width: 110 }}>{t('inv.status')}</TableCell>
                      <TableCell sx={{ width: 200 }}>{t('inv.colUnit')}</TableCell>
                      <TableCell align="right" sx={{ width: 140 }}>
                        {t('inv.colTotal')}
                      </TableCell>
                      <TableCell sx={{ width: 130 }}>{t('inv.colIssued')}</TableCell>
                      <TableCell sx={{ width: 130 }}>{t('inv.colDue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((inv) => (
                      <TableRow key={inv.id} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            component={RouterLink}
                            to={`/invoices/${inv.id}`}
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {inv.number}
                          </Typography>
                          {inv.source !== 'MANUAL' ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {inv.source}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <InvoiceStatusChip status={inv.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {inv.unit_id}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatRupiah(inv.total_cents)}
                          </Typography>
                          {inv.paid_cents > 0 && inv.paid_cents < inv.total_cents ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('inv.paidAmount', { amount: formatRupiah(inv.paid_cents) })}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(inv.issue_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(inv.due_date)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {nextCursor ? (
                <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                  <Button onClick={loadMore} disabled={loadingMore}>
                    {loadingMore ? t('common.loadingEllipsis') : t('inv.loadMore')}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
